/**
 * Countdown timer web component.
 *
 * Counts down to a fixed instant in time. The deadline is supplied as an ISO 8601
 * string that already carries a UTC offset (e.g. 2026-09-28T20:00:00+05:30), so the
 * target instant is absolute: every visitor counts down to the same moment no matter
 * what timezone their device is set to.
 */

const UNITS = ['days', 'hours', 'minutes', 'seconds'];

class CountdownTimer extends HTMLElement {
  #timeoutId = null;
  #deadline = null;
  #expired = false;
  #lastAnnouncedMinute = null;

  connectedCallback() {
    const raw = this.getAttribute('deadline');
    const parsed = raw ? Date.parse(raw) : NaN;

    if (Number.isNaN(parsed)) {
      // Invalid or missing date: leave the placeholder markup alone rather than
      // rendering "NaN". The theme editor shows the merchant a warning instead.
      this.setAttribute('data-invalid', '');
      return;
    }

    this.#deadline = parsed;
    this.#tick();

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    this.#clearTimer();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  // Stop the interval while the tab is hidden, then resync immediately on return so
  // the display never shows a stale value after the tab is refocused.
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#clearTimer();
    } else if (!this.#expired) {
      this.#tick();
    }
  };

  #clearTimer() {
    if (this.#timeoutId != null) {
      clearTimeout(this.#timeoutId);
      this.#timeoutId = null;
    }
  }

  #tick = () => {
    const remaining = this.#deadline - Date.now();

    if (remaining <= 0) {
      this.#render({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      this.#handleExpiry();
      return;
    }

    this.#render(CountdownTimer.#breakdown(remaining));

    // Re-run on the next whole-second boundary instead of a fixed 1000ms interval,
    // which would drift over a countdown this long.
    this.#clearTimer();
    this.#timeoutId = setTimeout(this.#tick, 1000 - (Date.now() % 1000));
  };

  static #breakdown(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    return {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor(totalSeconds / 3600) % 24,
      minutes: Math.floor(totalSeconds / 60) % 60,
      seconds: totalSeconds % 60,
    };
  }

  #render(values) {
    for (const unit of UNITS) {
      const target = this.querySelector(`[data-countdown-value="${unit}"]`);
      if (!target) continue;

      // Days can exceed two digits on a long countdown; the rest stay zero-padded.
      const next = unit === 'days'
        ? String(values.days).padStart(2, '0')
        : String(values[unit]).padStart(2, '0');

      if (target.textContent !== next) target.textContent = next;
    }

    this.#announce(values);
  }

  // The digits are aria-hidden and change every second, which would flood a screen
  // reader. Instead the live region carries a plain-language summary, refreshed only
  // when the minute changes.
  #announce(values) {
    const liveRegion = this.querySelector('[data-countdown-live]');
    if (!liveRegion) return;

    if (this.#lastAnnouncedMinute === values.minutes) return;
    this.#lastAnnouncedMinute = values.minutes;

    const template = this.getAttribute('announce-template');
    if (!template) return;

    liveRegion.textContent = template
      .replace('[days]', values.days)
      .replace('[hours]', values.hours)
      .replace('[minutes]', values.minutes);
  }

  #handleExpiry() {
    if (this.#expired) return;
    this.#expired = true;
    this.#clearTimer();
    this.setAttribute('data-expired', '');

    const liveRegion = this.querySelector('[data-countdown-live]');
    const expiredText = this.getAttribute('expired-text');
    if (liveRegion && expiredText) liveRegion.textContent = expiredText;

    this.dispatchEvent(new CustomEvent('countdown:expired', { bubbles: true }));
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
